import { Box, Grid, Typography } from "@material-ui/core"
import { AccordionSaurus } from "views/components/accordions/accordion-saurus/accordion-saurus"
import { memo, useState, useMemo, useCallback } from "react"
import { useStyles } from "./accordion-historico-atualizacoes-styles"
import { toDateString } from "utils/to-date"
import { HistoricoVersaoLogsProps } from "../../historico-atualizacoes-list-data"
import { LogLogModel } from "model/api/gestao/clickup/log-log-model"
import { useThemeQueries } from "views/theme"
import { CardHistoricoAtualizacoes } from "views/components/cards/card-historico-atualizacoes/card-historico-atualizacoes"
import { DataVersao } from "./components/data-versao/data-versao"

interface AccordionHistoricoAtualizacoesProps {
    backlog: HistoricoVersaoLogsProps
    index: number
}

export const AccordionHistoricoAtualizacoesVersao = memo(({ backlog, index }: AccordionHistoricoAtualizacoesProps) => {
    const classes = useStyles()
    const { theme: { palette } } = useThemeQueries()

    const [expanded, setExpanded] = useState(() => index < 5);

    // Memoiza a função de alternar o estado de expansão
    const handleExpansionChange = useCallback(() => {
        setExpanded(prev => !prev);
    }, []);

    // Memoiza as datas únicas das tarefas
    const tarefaData = useMemo(() =>
        Array.from(new Set(backlog.logs.map(log => toDateString(log.data))))
        , [backlog.logs]);

    // Memoiza as datas processadas e ordenadas
    const processedDates = useMemo(() =>
        tarefaData.map(dateStr => ({
            original: dateStr,
            date: (() => {
                // Converte "DD/MM/YYYY" para Date
                const [day, month, year] = (dateStr ?? '').split('/');
                return new Date(Number(year), Number(month) - 1, Number(day));
            })()
        }))
            .sort((a, b) => b.date.getTime() - a.date.getTime())
        , [tarefaData]);

    // Memoiza o mapeamento das tarefas por data
    const tarefasPorData = useMemo(() => {
        const mapa = new Map();
        tarefaData.forEach(logDate => {
            mapa.set(
                logDate,
                backlog.logs
                    .filter(tarefa => toDateString(tarefa.data) === logDate)
                    .sort((a, b) => new Date(b.data).getTime() - new Date(a.data).getTime())
            );
        });
        return mapa;
    }, [backlog.logs, tarefaData]);

    return (
        <Box pt={index > 0 ? 1 : 0}>
            <AccordionSaurus
                labelPrimary={
                    <Typography className={classes.labelPrimary}>
                        <span>{backlog.versao.descricao}</span>
                    </Typography>
                }
                expanded={expanded}
                showDivider
                colorExpand={palette.primary.main}
                tipoExpand="bold"
                classesTitle={classes.titleAccordion}
                onChange={handleExpansionChange}
            >
                {expanded && (
                    <Grid container spacing={2}>
                        <Grid item xs={12} className={classes.contentDataVersao}>
                            <DataVersao data={backlog.versao.data_alfa} tipo='alfa' />
                            <DataVersao data={backlog.versao.data_beta} tipo='beta' />
                            <DataVersao data={backlog.versao.data_estavel} tipo='estavel' />
                        </Grid>
                        {processedDates.map(({ original: log }) => {
                            const tarefas = tarefasPorData.get(log);
                            return (
                                <Grid item xs={12} key={log}>
                                    <Grid className={classes.tarefasContainer}>
                                        <Typography variant='body1' className={classes.titulo}>
                                            • Desenvolvido em <span>{log}</span>
                                        </Typography>
                                        <Grid className={classes.tarefas}>
                                            {tarefas.map((tarefa: LogLogModel) => {
                                                return (
                                                    <CardHistoricoAtualizacoes
                                                        descricao={tarefa.descricao}
                                                        link_clickup={tarefa.link_clickup}
                                                        tipo_log={tarefa.tipo_log}
                                                        titulo={tarefa.titulo}
                                                        key={tarefa.id}
                                                    />
                                                )
                                            })}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )
                        })}
                    </Grid>
                )
                }
            </AccordionSaurus>
        </Box>
    )
})

AccordionHistoricoAtualizacoesVersao.displayName = 'AccordionHistoricoAtualizacoesVersao'